/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React from 'react';
import Autosuggest, { InputProps, SuggestionSelectedEventData } from 'react-autosuggest';
import { escapeRegexCharacters } from '../../utils/string-format';
import './SearchComponent.scss';

export interface Suggestion {
  _id: string;
  name: string;
  jobTitle?: string;
  profile?: string;
  email?: string;
}

export interface SearchComponentProps<T = Suggestion> {
  name?: string;
  placeholder: string;
  inputClassName?: string;
  disabled?: boolean;
  required?: boolean;
  autoComplete?: string;
  defaultValue?: string;
  searchAction?: (searchStr: string) => Promise<T[]>;
  handleValueChange: (searchStr: string, currentSuggestion?: T) => void;
  clearMessageCallback?: () => void;
  renderSuggestion: (suggestion: T) => JSX.Element;
  handleSuggestionSelected?: (suggestion: T) => void;
}

interface SearchComponentState {
  message: string;
  error: boolean;
  saving: boolean;
  escapedString: string;
  valueId: string;
  suggestions: Suggestion[],
}

class SearchComponent extends React.Component<SearchComponentProps, SearchComponentState> {

  state: SearchComponentState = {
    message: '',
    error: false,
    saving: false,
    escapedString: '',
    valueId: '',
    suggestions: [],
  };

  public componentDidMount() {
    if (this.props.defaultValue) {
      this.setState({ valueId: this.props.defaultValue})
    }
  }

  onChange = (event: React.FormEvent<HTMLElement>, params: Autosuggest.ChangeEvent) => {
    this.setState(
      { valueId: params.newValue },
      () => this.props.handleValueChange(params.newValue, this.getCurrentSuggestion())
    );
  };

  onSuggestionsFetchRequested = (v: { value: string, reason: string }) => {
    if (v.reason === 'input-changed') {
      this.search(v.value);
    }
  };

  onSuggestionsClearRequested = () => {};

  onSuggestionSelected = (
    event: React.FormEvent<any>,
    data: SuggestionSelectedEventData<Suggestion>,
) => {
    if (this.props.handleSuggestionSelected) {
      this.props.handleSuggestionSelected(data.suggestion);
      this.clear();
    }
  }

  handleError = (e: { message: string }) => {
    this.setState({
      message: e.message,
      error: true,
      saving: false
    });
  };

  search(str: string) {
    if (!this.props.searchAction) {
      return;
    }
    const escapedString = escapeRegexCharacters(str.trim());

    if (escapedString === '' || escapedString === this.state.escapedString) {
      return;
    }

    if (this.props.clearMessageCallback) {
      this.props.clearMessageCallback();
    }

    this.setState({ escapedString: escapedString });

    return this.props.searchAction(escapedString)
      .then(suggestions => this.setState({ suggestions }))
      .catch(this.handleError);
  }

  getSuggestionValue(suggestion: Suggestion) {
    return suggestion._id;
  }

  getCurrentSuggestion() {
    const { valueId, suggestions } = this.state;
    return suggestions.find((suggestion) => suggestion._id === valueId);
  }

  clear() {
    this.setState({suggestions: [], valueId: '', escapedString: ''});
  }

  render() {
    const { valueId, suggestions } = this.state;
    const { inputClassName, required, name, autoComplete } = this.props;

    const valueSuggestion = this.getCurrentSuggestion();
    const inputProps: InputProps<Suggestion> = {
      placeholder: this.props.placeholder,
      value: valueSuggestion ? valueSuggestion.name : valueId,
      onChange: this.onChange,
      className: inputClassName,
      disabled: !!this.props.disabled,
      required: !!required,
      name,
      autoComplete,
    };

    return <div className='searchbox-container d-flex'>
      <div className='flex-fill'>

      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
        onSuggestionSelected={this.onSuggestionSelected}
        getSuggestionValue={this.getSuggestionValue}
        renderSuggestion={this.props.renderSuggestion}
        inputProps={inputProps} />
      </div>
    </div>
  }
}

export default SearchComponent;
